import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import { Icon } from '../../components/ui/Icon';
import TierBreakdownBar from '../../components/ui/TierBreakdownBar';
import EvaluateDialog from './EvaluateDialog';
import useJobWatcher from '../../hooks/useJobWatcher';

import {
  fetchPeriod,
  activatePeriod,
  closePeriod,
  reopenPeriod,
} from '../../store/slices/periodsSlice';
import { fetchTasks } from '../../store/slices/tasksSlice';
import { fetchSegments, fetchMudurlukler } from '../../store/slices/referencesSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { formatDate, formatASPara } from '../../utils/format';
import { METRIC_TYPES } from '../../constants/navigation';
import apiClient from '../../services/apiClient';

const STATUS_STYLES = {
  active: { color: 'green', label: 'Aktif' },
  closed: { color: 'neutral', label: 'Kapalı' },
  draft: { color: 'yellow', label: 'Taslak' },
};

export default function PeriodDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const period = useSelector((s) => s.periods.selected);
  const tasks = useSelector((s) => s.tasks.items.filter((t) => t.game_period_id === Number(id)));
  const { segments, segmentsStatus } = useSelector((s) => s.references);
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  // Hesaplama bitince başlık, görev kademeleri, sıralama ve deneme hesabı
  // panelleri sayfa yenilemeden tazelensin; paneller refreshKey'e bağlı.
  const [refreshKey, setRefreshKey] = useState(0);
  const watchJob = useJobWatcher();

  useEffect(() => {
    dispatch(fetchPeriod(id));
    dispatch(fetchTasks({ period_id: id }));
    if (segmentsStatus === 'idle') dispatch(fetchSegments({ include_inactive: false }));
  }, [dispatch, id, segmentsStatus]);

  if (!period) {
    return <div className="text-sm text-neutral-500">Yükleniyor…</div>;
  }

  const statusKey = (period.status || '').toLowerCase();
  const status = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
  const metricLabel = (mt) => METRIC_TYPES.find((x) => x.value === mt)?.label || mt;

  // Kapalı dönem yeniden açılabilir mi? end_date bugün veya ilerideyse evet.
  const canReopen =
    statusKey === 'closed' &&
    period.end_date &&
    new Date(period.end_date) >= new Date(new Date().toDateString());

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={period.name}
        subtitle={period.description}
        // backTo=-1 → tarayıcı geçmişinde bir adım geri. Bu sayfaya hem
        // /periods listesinden hem dashboard'daki "Tüm sonuçlar →" linkinden
        // gelinebiliyor; her durumda doğru yere döner.
        backTo={-1}
        breadcrumbs={[
          { label: 'Dönem Yönetimi', to: '/periods' },
          { label: period.name },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Badge color={status.color} size="lg">
              {status.label}
            </Badge>
            {/* Düzenle — tüm statülerde erişilebilir */}
            <Button
              variant="secondary"
              icon="edit"
              onClick={() => navigate('/periods', { state: { editId: period.id } })}
            >
              Düzenle
            </Button>
            {statusKey === 'draft' && (
              <Button
                variant="success"
                onClick={() =>
                  dispatch(activatePeriod(period.id)).then(() =>
                    dispatch(pushToast({ kind: 'success', message: 'Dönem aktifleştirildi' })),
                  )
                }
              >
                Aktifleştir
              </Button>
            )}
            {statusKey === 'active' && (
              <>
                <Button variant="secondary" onClick={() => setEvaluateOpen(true)}>
                  Hesapla
                </Button>
                <Button variant="danger" onClick={() => setCloseConfirmOpen(true)}>
                  Dönemi Kapat
                </Button>
              </>
            )}
            {canReopen && (
              <Button
                variant="warning"
                icon="refresh"
                onClick={() =>
                  dispatch(reopenPeriod(period.id)).then((action) => {
                    if (reopenPeriod.fulfilled.match(action)) {
                      dispatch(pushToast({ kind: 'success', message: 'Dönem yeniden açıldı' }));
                    } else {
                      dispatch(pushToast({ kind: 'error', message: action.payload || 'Dönem açılamadı' }));
                    }
                  })
                }
              >
                Yeniden Aç
              </Button>
            )}
          </div>
        }
      />

      {/* Dönem Bilgileri + Ödül Şablonu — shared rt extraction */}
      {(() => {
        const rt = period.reward_template || {};
        const _legacyScope = (rt.scope || 'bolge').toLowerCase();
        const bolgeTiers = rt.bolge_tiers ?? (_legacyScope === 'bolge' ? (rt.tiers || []) : []);
        const genelTiers = rt.genel_tiers ?? (_legacyScope === 'genel' ? (rt.tiers || []) : []);
        const perMudurluk = rt.per_mudurluk || {};
        const hasMudurluk = Object.keys(perMudurluk).length > 0;
        const mgrTiers = rt.manager_tiers || [];
        const mgrBaraj = rt.manager_baraj_min_sks ?? null;
        const rawBA = rt.badge_awards;
        const badgeAwards = Array.isArray(rawBA)
          ? rawBA
          : rawBA && typeof rawBA === 'object'
            ? [
                ...(rawBA.bolge   || []).map((a) => ({ ...a, scope: 'Bölge' })),
                ...(rawBA.genel   || []).map((a) => ({ ...a, scope: 'Genel' })),
                ...(rawBA.manager || []).map((a) => ({ ...a, scope: 'Yönetici' })),
              ]
            : [];
        const hasMgr = mgrTiers.length > 0 || mgrBaraj != null || (rt.manager_baraj_min_hyo ?? 0) > 0;
        const hasRewards = bolgeTiers.length || genelTiers.length || hasMudurluk || mgrTiers.length || badgeAwards.length;

        const TierRow = ({ r, bg = 'bg-neutral-50' }) => (
          <li className={`flex items-center justify-between rounded ${bg} px-2.5 py-1.5`}>
            <span className={`text-xs font-semibold ${
              r.rank_from === 1 ? 'text-amber-500'
              : r.rank_from === 2 ? 'text-neutral-400'
              : r.rank_from === 3 ? 'text-amber-700'
              : 'text-neutral-500'
            }`}>
              {r.rank_from === r.rank_to ? `#${r.rank_from}` : `#${r.rank_from}–${r.rank_to}`}
            </span>
            <span className="text-xs font-semibold text-neutral-700">
              {Number(r.aspara).toLocaleString('tr-TR')} ASPara
            </span>
          </li>
        );

        return (
          <>
            {/* ── Dönem Bilgileri — tam genişlik ── */}
            <Card>
              <CardHeader title="Dönem Bilgileri" />
              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Başlangıç</dt>
                  <dd className="mt-0.5 font-medium">{formatDate(period.start_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Bitiş</dt>
                  <dd className="mt-0.5 font-medium">{formatDate(period.end_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Kullanıcı Tipi</dt>
                  <dd className="mt-0.5 flex flex-wrap gap-1">
                    {(Array.isArray(period.segment_ids) ? period.segment_ids : []).length > 0 ? (
                      period.segment_ids.map((sid) => {
                        const seg = segments.find((s) => String(s.id) === String(sid));
                        return (
                          <Badge key={sid} color="blue" size="sm">
                            {seg ? seg.name : sid}
                          </Badge>
                        );
                      })
                    ) : (
                      <span className="text-xs text-neutral-400">tenant geneli</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Baraj / Max Puan</dt>
                  <dd className="mt-0.5 font-medium">
                    {period.min_total_points} / {period.max_total_points}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Puan → ASPara</dt>
                  <dd className="mt-0.5 font-medium">
                    {Number(period.aspara_per_point || 0) > 0
                      ? `1p = ${Number(period.aspara_per_point).toLocaleString('tr-TR', { maximumFractionDigits: 4 })} ASPara`
                      : <span className="text-xs text-neutral-400">— (yalnızca rank ödülü)</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">Görev Sayısı</dt>
                  <dd className="mt-0.5 font-medium">{tasks.length} görev</dd>
                </div>
                {/* Yönetici oyunlaştırma — reward_template'te tanımlıysa göster */}
                {hasMgr && mgrBaraj != null && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-neutral-500">Yön. Min. SKS</dt>
                    <dd className="mt-0.5 font-medium">{mgrBaraj} SKS</dd>
                  </div>
                )}
                {hasMgr && (rt.manager_baraj_min_hyo ?? 0) > 0 && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-neutral-500">Yön. Min. HYO</dt>
                    <dd className="mt-0.5 font-medium">%{rt.manager_baraj_min_hyo}</dd>
                  </div>
                )}
              </dl>
            </Card>

            {/* ── Ödül Şablonu — 3 kolon ── */}
            <Card>
              <CardHeader title="Ödül Şablonu" />
              {!hasRewards ? (
                <p className="text-sm text-neutral-400">Henüz ödül tanımlanmamış</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {/* Kolon 1: SKS Grup İçi */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                        SKS — Grup İçi
                      </p>
                      {(bolgeTiers.length > 0 || hasMudurluk) ? (
                        <div className="space-y-2">
                          {bolgeTiers.length > 0 && (
                            <div>
                              {hasMudurluk && (
                                <p className="text-xs font-medium text-neutral-500 mb-1.5">Varsayılan</p>
                              )}
                              <ul className="space-y-1.5">
                                {bolgeTiers.map((r, i) => <TierRow key={i} r={r} />)}
                              </ul>
                            </div>
                          )}
                          {hasMudurluk && (
                            <div>
                              <p className="text-xs font-medium text-neutral-500 mb-1.5">Müdürlük Özel</p>
                              {Object.entries(perMudurluk).map(([mudurluk, mtiers]) => (
                                <div key={mudurluk} className="mb-2">
                                  <p className="text-[10px] text-neutral-500 font-mono mb-1 truncate" title={mudurluk}>{mudurluk}</p>
                                  <ul className="space-y-1">
                                    {(mtiers || []).map((r, i) => (
                                      <TierRow key={i} r={r} bg="bg-amber-50" />
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-neutral-400">Tanımlanmamış</p>
                      )}
                    </div>

                    {/* Kolon 2: SKS Türkiye Geneli */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                        SKS — Türkiye Geneli
                      </p>
                      {genelTiers.length > 0 ? (
                        <ul className="space-y-1.5">
                          {genelTiers.map((r, i) => <TierRow key={i} r={r} bg="bg-green-50" />)}
                        </ul>
                      ) : (
                        <p className="text-xs text-neutral-400">Tanımlanmamış</p>
                      )}
                    </div>

                    {/* Kolon 3: Yönetici */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                        Yönetici (HYO)
                      </p>
                      {mgrTiers.length > 0 ? (
                        <>
                          {(mgrBaraj != null || (rt.manager_baraj_min_hyo ?? 0) > 0) && (
                            <div className="flex flex-wrap gap-1.5 text-[10px] text-neutral-500 mb-2">
                              {mgrBaraj != null && (
                                <span className="rounded bg-neutral-100 px-1.5 py-0.5">
                                  Min. SKS: <strong>{mgrBaraj}</strong>
                                </span>
                              )}
                              {(rt.manager_baraj_min_hyo ?? 0) > 0 && (
                                <span className="rounded bg-neutral-100 px-1.5 py-0.5">
                                  Min. HYO: <strong>%{rt.manager_baraj_min_hyo}</strong>
                                </span>
                              )}
                            </div>
                          )}
                          <ul className="space-y-1.5">
                            {mgrTiers.map((r, i) => (
                              <TierRow key={i} r={r} bg="bg-blue-50" />
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p className="text-xs text-neutral-400">Tanımlanmamış</p>
                      )}
                    </div>
                  </div>

                  {/* Rozet ödülleri — tam genişlik, altında */}
                  {badgeAwards.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                        Rozet Ödülleri
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {badgeAwards.map((a, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 rounded bg-purple-50 border border-purple-100 px-2.5 py-1.5"
                          >
                            <BadgeThumbSmall badgeId={a.badge_id} />
                            <span className="text-xs text-neutral-600">
                              {a.rank_from === a.rank_to
                                ? `#${a.rank_from}`
                                : `#${a.rank_from}–${a.rank_to}`}
                              {a.scope && (
                                <span className="ml-1.5 text-neutral-400">({a.scope})</span>
                              )}
                            </span>
                            <span className="text-[10px] font-mono text-neutral-400">
                              #{a.badge_id}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          </>
        );
      })()}

      <Card padded={false}>
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div>
            <h3 className="text-base font-semibold">Bu Dönemin Görevleri</h3>
            <p className="text-xs text-neutral-500">Bu döneme bağlı rule_set tanımları</p>
          </div>
          <Button
            variant="secondary"
            icon="arrowRight"
            onClick={() => navigate(`/tasks?period_id=${period.id}`)}
          >
            Görevlere Git
          </Button>
        </div>
        <Table
          columns={[
            {
              key: 'name',
              header: 'Görev',
              render: (r) => (
                <div>
                  <span className="font-medium">{r.name}</span>
                  {r.is_joker && (
                    <span className="ml-1.5 text-[10px] bg-purple-100 text-purple-700 rounded px-1 py-0.5 font-medium">Joker</span>
                  )}
                </div>
              ),
            },
            {
              key: 'status',
              header: 'Durum',
              render: (r) => {
                const TASK_STATUS = {
                  ACTIVE:    { color: 'green',  label: 'Aktif' },
                  INACTIVE:  { color: 'neutral', label: 'Pasif' },
                  CANCELLED: { color: 'red',    label: 'İptal' },
                  COMPLETED: { color: 'blue',   label: 'Tamamlandı' },
                };
                const s = TASK_STATUS[r.status] || { color: 'neutral', label: r.status };
                return <Badge color={s.color} size="sm">{s.label}</Badge>;
              },
            },
            {
              key: 'metric_type',
              header: 'Metrik',
              render: (r) => {
                const mt = METRIC_TYPES.find((x) => x.value === r.metric_type);
                return <Badge color={mt?.color || 'neutral'}>{mt?.label || r.metric_type}</Badge>;
              },
            },
            {
              key: 'source_table_label',
              header: 'Kaynak',
              render: (r) => {
                const tbl = r.source?.ch_table;
                const col = r.source?.metric_column;
                if (!tbl) return <span className="text-xs text-neutral-400">—</span>;
                // Not: Backend'in TABLE_LABELS'i ile senkron tutulmali —
                // FastApiRuleEngineAS/utils/clickhouse.py
                const LABELS = {
                  as_uretim: 'Üretim', as_yenileme: 'Yenileme',
                  as_prim: 'Prim', as_saglik: 'Sağlık',
                  as_grup_saglik: 'Grup Sağlık', as_teklif: 'Teklif',
                  as_ziyaret: 'Ziyaret', as_rota_hedefler: 'Rota Hedef',
                  as_hedef_musteri: 'Hedef Müşteri', as_kanal_bilgisi: 'Kanal',
                };
                return (
                  <div>
                    <span className="text-xs font-medium text-neutral-700">{LABELS[tbl] || tbl}</span>
                    {col && <p className="text-[10px] text-neutral-400">{col}</p>}
                  </div>
                );
              },
            },
            {
              key: 'effective_dates',
              header: 'Tarih Aralığı',
              render: (r) => {
                if (!r.effective_start_date && !r.effective_end_date)
                  return <span className="text-xs text-neutral-400">Dönem geneli</span>;
                return (
                  <span className="text-xs text-neutral-600">
                    {r.effective_start_date ? formatDate(r.effective_start_date) : '—'}
                    {' → '}
                    {r.effective_end_date ? formatDate(r.effective_end_date) : '—'}
                  </span>
                );
              },
            },
            {
              key: 'max_points',
              header: 'Max Puan',
              render: (r) => <span className="font-medium">{r.max_points}</span>,
            },
            {
              key: 'completion',
              header: 'Kademe İlerlemesi',
              render: (r) => {
                // CANCELLED görevde tier breakdown anlamlı değil — sadece "İptal" yaz.
                if (r.status === 'CANCELLED') {
                  return (
                    <span className="text-xs font-medium text-neutral-400 line-through">
                      İptal
                    </span>
                  );
                }
                // tier_breakdown geliyor mu? (backend list endpoint döner)
                const tiers = Array.isArray(r.tier_breakdown) ? r.tier_breakdown : [];
                if (tiers.length === 0) {
                  // Tier'sız görev (MANUAL vs.) → eski yüzdelik fallback.
                  const pct = r.completion_percent;
                  if (pct == null) {
                    return <span className="text-xs text-neutral-400">—</span>;
                  }
                  return (
                    <span className="text-xs font-medium text-neutral-700">
                      %{pct}
                    </span>
                  );
                }
                // Görev listesi + dashboard ile birebir aynı görünüm
                return (
                  <div className="min-w-[180px]">
                    <TierBreakdownBar tiers={tiers} />
                    {r.completion_percent != null && (
                      <span className="text-[9px] text-neutral-400 mt-1 block">
                        Katılım: %{r.completion_percent}
                      </span>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={[...tasks].sort((a, b) => {
            // ACTIVE üstte, iptal/pasif altta
            const ORDER = { ACTIVE: 0, COMPLETED: 1, INACTIVE: 2, CANCELLED: 3 };
            return (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9);
          })}
          emptyMessage="Bu dönem için görev tanımlanmamış"
        />
      </Card>

      {/* Sıralama — aktif ve kapalı dönemlerde göster */}
      {(statusKey === 'active' || statusKey === 'closed') && (
        <LeaderboardPanel period={period} refreshKey={refreshKey} />
      )}

      {/* Dry-Run Simülasyon Sonuçları */}
      {(statusKey === 'active' || statusKey === 'closed') && (
        <DryRunPanel period={period} refreshKey={refreshKey} />
      )}

      <EvaluateDialog
        period={evaluateOpen ? period : null}
        onClose={() => setEvaluateOpen(false)}
        onJobQueued={({ jobId, dryRun }) =>
          watchJob(jobId, {
            label: `"${period.name}" ${dryRun ? 'deneme hesabı' : 'hesaplaması'}`,
            onDone: () => {
              dispatch(fetchPeriod(id));
              dispatch(fetchTasks({ period_id: id }));
              setRefreshKey((k) => k + 1);
            },
          })
        }
      />

      {/* Dönemi Kapat — onay modalı */}
      {closeConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 rounded-full bg-red-100 p-2">
                <Icon name="alertCircle" className="w-5 h-5 text-red-600" />
              </div>
              <div className="space-y-3">
                <div>
                  <h2 className="text-base font-semibold text-neutral-900">Dönemi Kapat</h2>
                  <p className="mt-1 text-sm text-neutral-600">
                    <strong>{period.name}</strong> dönemi kapatılacak. Gerekirse dönem bitiş tarihi geçmeden yeniden açılabilir.
                  </p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1.5">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                    <Icon name="alert-triangle" className="h-3.5 w-3.5" />
                    T-1 veri akışını kontrol edin
                  </p>
                  <ul className="text-xs text-amber-900 space-y-0.5 pl-4 list-disc">
                    <li>
                      Dönem bitiş tarihi ({period.end_date ? new Date(period.end_date).toLocaleDateString('tr-TR') : '—'})
                      verisi ancak <strong>bir sonraki gün</strong> gelir.
                    </li>
                    <li>Günlük cron'un tamamlandığını teyit etmeden kapatmayın.</li>
                    <li>Erken kapatılırsa son günün verileri <strong>hesaba girmez</strong>.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setCloseConfirmOpen(false)}>
                Vazgeç
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setCloseConfirmOpen(false);
                  dispatch(closePeriod(period.id)).then((action) => {
                    if (closePeriod.fulfilled.match(action)) {
                      dispatch(pushToast({ kind: 'info', message: 'Dönem kapatıldı' }));
                    } else {
                      dispatch(pushToast({ kind: 'error', message: action.payload || 'Dönem kapatılamadı' }));
                    }
                  });
                }}
              >
                Evet, Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardPanel — dönem detayına gömülü mini sıralama bölümü
// ---------------------------------------------------------------------------

function RankBadgeMini({ rank }) {
  const inner =
    rank === 1 ? <span className="text-sm font-bold text-amber-500">#{rank}</span> :
    rank === 2 ? <span className="text-sm font-bold text-neutral-400">#{rank}</span> :
    rank === 3 ? <span className="text-sm font-bold text-amber-700">#{rank}</span> :
                 <span className="text-sm text-neutral-500">#{rank}</span>;
  return <span className="inline-flex w-8 items-center justify-center">{inner}</span>;
}

function TierProgressBar({ t }) {
  const raw = t.raw_value ?? 0;
  const cur = t.tier_threshold;
  const next = t.next_tier_threshold;

  // İlerleme yüzdesi: mevcut tier eşiğine göre sonraki eşiğe ne kadar kaldı
  let pct = 0;
  if (next != null) {
    const base = cur ?? 0;
    pct = base >= next ? 100 : Math.min(((raw - base) / (next - base)) * 100, 100);
    pct = Math.max(pct, 0);
  } else if (cur != null) {
    pct = 100; // En üst tier'da
  }

  const barColor = cur != null ? 'bg-primary-500' : 'bg-amber-400';

  return (
    <div className="mt-1.5 space-y-0.5">
      <div className="flex items-center justify-between text-[10px] text-neutral-400">
        <span>{cur != null ? `Eşik: ${cur.toLocaleString('tr-TR')}` : 'Henüz eşik yok'}</span>
        {next != null
          ? <span className="text-amber-600 font-medium">Sonraki: {next.toLocaleString('tr-TR')} (+{t.next_tier_points?.toFixed(1)}p)</span>
          : <span className="text-success-600 font-medium">Maksimum tier ✓</span>}
      </div>
      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ManagerHyoDashboard({ md }) {
  if (!md) return <p className="text-xs text-neutral-400 py-4 text-center">Yönetici verisi bulunamadı.</p>;

  const hyoPct = md.hyo_yuzde ?? 0;
  const minHyo = md.min_hyo_baraj ?? 0;
  const barMax = Math.max(hyoPct, minHyo, 100);
  const hyoBarPct = Math.min((hyoPct / barMax) * 100, 100);
  const minBarPct = Math.min((minHyo / barMax) * 100, 100);
  const hyoColor = hyoPct >= minHyo ? 'bg-success-500' : 'bg-red-400';

  return (
    <div className="space-y-4">
      {/* HYO progress */}
      <div className="rounded-xl border border-neutral-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-neutral-700">Hedef Yüzdesi Oranı (HYO)</p>
          <span className={`text-lg font-bold ${hyoPct >= minHyo ? 'text-success-700' : 'text-red-600'}`}>
            %{hyoPct.toFixed(1)}
          </span>
        </div>
        <div className="relative h-3 rounded-full bg-neutral-100 overflow-visible">
          <div className={`h-full rounded-full ${hyoColor}`} style={{ width: `${hyoBarPct}%` }} />
          {/* Baraj çizgisi */}
          {minHyo > 0 && (
            <div
              className="absolute top-0 h-full w-0.5 bg-red-400"
              style={{ left: `${minBarPct}%` }}
              title={`Baraj: %${minHyo}`}
            />
          )}
        </div>
        <div className="flex items-center justify-between text-[10px] text-neutral-400">
          <span>Gerçekleşen: <strong className="text-neutral-700">{md.gerceklesen_puan?.toLocaleString('tr-TR')}</strong></span>
          <span>Hedef: <strong className="text-neutral-700">{md.hedef_puan?.toLocaleString('tr-TR')}</strong></span>
          {minHyo > 0 && <span className="text-red-400">Min. baraj: %{minHyo}</span>}
        </div>
      </div>

      {/* SKS sayıları */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-neutral-50 px-4 py-3 text-center">
          <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Toplam SKS</p>
          <p className="text-2xl font-bold text-neutral-800 mt-0.5">{md.sks_count}</p>
          {md.min_sks_baraj != null && (
            <p className="text-[10px] text-neutral-400 mt-0.5">Min. baraj: {md.min_sks_baraj}</p>
          )}
        </div>
        <div className={`rounded-xl px-4 py-3 text-center ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'bg-success-50' : 'bg-red-50'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'text-success-500' : 'text-red-400'}`}>
            Baraj Geçen
          </p>
          <p className={`text-2xl font-bold mt-0.5 ${md.baraj_gecen_sks >= (md.min_sks_baraj ?? 0) ? 'text-success-700' : 'text-red-600'}`}>
            {md.baraj_gecen_sks}
            <span className="text-sm font-normal text-neutral-400 ml-1">/ {md.sks_count}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function UserDetailModal({ open, onClose, detail, loading, tab }) {
  if (!open) return null;
  const snap = detail?.snapshot;
  const taskScores = detail?.task_scores ?? [];
  const managerDetail = detail?.manager_detail ?? null;
  const badgeAwards = detail?.badge_awards ?? [];
  const isManager = tab === 'manager';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <p className="text-base font-semibold text-neutral-900">
              {snap?.user_name_masked || '—'}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              {snap?.user_id}
              {snap?.rank != null && (
                <span className="ml-2">Genel Sıra: <span className="font-semibold text-neutral-700">#{snap.rank}</span></span>
              )}
              {!isManager && snap?.rank_bolge != null && (
                <span className="ml-2">Grup İçi Sıra: <span className="font-semibold text-neutral-700">#{snap.rank_bolge}</span></span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 transition-colors">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              {/* SKS özet kartları */}
              {!isManager && snap && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-primary-50 px-4 py-3 text-center">
                    <p className="text-[10px] text-primary-500 uppercase tracking-wide">Toplam Puan</p>
                    <p className="text-xl font-bold text-primary-700 mt-0.5">{snap.total_points?.toFixed(1)}</p>
                  </div>
                  <div className="rounded-xl bg-success-50 px-4 py-3 text-center">
                    <p className="text-[10px] text-success-500 uppercase tracking-wide">Tahmini ASPara</p>
                    <p className="text-xl font-bold text-success-700 mt-0.5">
                      {snap.estimated_aspara > 0 ? formatASPara(snap.estimated_aspara) : '—'}
                    </p>
                  </div>
                  <div className={`rounded-xl px-4 py-3 text-center ${snap.below_baraj ? 'bg-red-50' : 'bg-neutral-50'}`}>
                    <p className={`text-[10px] uppercase tracking-wide ${snap.below_baraj ? 'text-red-400' : 'text-neutral-400'}`}>Baraj</p>
                    <p className={`text-base font-bold mt-0.5 ${snap.below_baraj ? 'text-red-600' : 'text-neutral-600'}`}>
                      {snap.below_baraj ? 'Baraj Altı' : 'Baraj Üstü'}
                    </p>
                  </div>
                </div>
              )}

              {/* MANAGER — HYO dashboard */}
              {isManager && <ManagerHyoDashboard md={managerDetail} />}

              {/* SKS — Görev bazlı puanlar + tier progress */}
              {!isManager && (
                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2">Görev Bazlı Puanlar</p>
                  {taskScores.length === 0 ? (
                    <p className="text-xs text-neutral-400 py-3 text-center">Henüz puan yok</p>
                  ) : (
                    <div className="rounded-xl border border-neutral-200 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-neutral-50 border-b border-neutral-200">
                            <th className="px-3 py-2 text-left font-semibold text-neutral-600">Görev</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Ham / Eşik</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Puan</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Bonus</th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">Toplam</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taskScores.map((t) => (
                            <tr key={t.task_id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                              <td className="px-3 py-2 max-w-[180px]">
                                <p className="font-medium text-neutral-800 truncate">{t.task_name}</p>
                                <p className="text-[10px] text-neutral-400">{t.metric_type}</p>
                              </td>
                              <td className="px-3 py-2 text-right align-top">
                                <p className="font-medium text-neutral-700">
                                  {t.raw_value != null ? t.raw_value.toLocaleString('tr-TR') : '—'}
                                </p>
                                <TierProgressBar t={t} />
                              </td>
                              <td className="px-3 py-2 text-right text-neutral-700 align-top">{t.points?.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right text-primary-600 align-top">
                                {t.bonus_points > 0 ? `+${t.bonus_points.toFixed(1)}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-primary-700 align-top">{t.total_points?.toFixed(1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Rozetler */}
              {badgeAwards.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2">Kazanılacak Rozetler</p>
                  <div className="flex flex-wrap gap-2">
                    {badgeAwards.map((b, i) => (
                      <span key={i} className="rounded-lg border border-purple-200 bg-purple-50 px-2.5 py-1 text-xs text-purple-700">
                        Rozet #{b.badge_id} · #{b.rank_from}–#{b.rank_to}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!snap && !loading && (
                <p className="text-xs text-neutral-400 py-8 text-center">Bu kullanıcı için veri bulunamadı.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaderboardPanel({ period, refreshKey }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tab, setTab] = useState('sks'); // 'sks' | 'manager'
  const [sksView, setSksView] = useState('genel'); // 'genel' | 'grupici'
  const [sksBolge, setSksBolge] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const SIZE = 10;
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, data: null, loading: false });

  // Grup İçi dropdown'unu tenant-specific müdürlük listesinden besle.
  //
  // Kaynak: GET /rule-engine/mudurlukler — public.as_user_registry'den
  // DISTINCT sorumlu_mudurluk degerleri (JWE token'dan tenant_uuid alinarak
  // current_customer_uuid filtresi otomatik). Redux Slice fetchMudurlukler
  // thunk'i ile cache'li (session basina bir kez).
  //
  // Eskiden leaderboard satirlarindan distinct cekiliyordu — bu yontemin iki
  // problemi vardi: (1) size:500 limit ile temsil edilmemis mudurlukler
  // dropdown'da gorunmuyordu, (2) bolge/mudurluk alan onceligi Leaderboard ile
  // tutarsizdi (PeriodDetail mudurluk, Leaderboard bolge). Yeni yontem her
  // iki sayfada da ayni Redux state'ten besleniyor — tutarlilik garantili.
  const mudurluklerData   = useSelector((s) => s.references.mudurlukler);
  const mudurluklerStatus = useSelector((s) => s.references.mudurluklerStatus);
  useEffect(() => {
    if (mudurluklerStatus === 'idle') {
      dispatch(fetchMudurlukler({ only_active: true }));
    }
  }, [dispatch, mudurluklerStatus]);

  const bolgeOptions = (mudurluklerData || [])
    .map((m) => m.code)
    .filter(Boolean)
    .sort();

  const fetchRows = useCallback(async () => {
    if (!period?.id) return;
    // Grup içi görünümde bölge seçilmemişse fetch yapma
    if (tab === 'sks' && sksView === 'grupici' && !sksBolge) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const endpoint = tab === 'sks'
        ? '/rule-engine/leaderboard/sks'
        : '/rule-engine/leaderboard/manager';
      const params = { period_id: period.id, page, size: SIZE };
      if (tab === 'sks' && sksView === 'grupici' && sksBolge) params.bolge = sksBolge;
      const res = await apiClient.get(endpoint, { params });
      const data = res.data?.data ?? res.data ?? [];
      const pagination = res.data?.pagination ?? {};
      setRows(data);
      setTotal(pagination.total ?? data.length);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period?.id, tab, sksView, sksBolge, page]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  // Tab değişince sayfayı sıfırla
  const handleTabChange = (t) => {
    setTab(t);
    setPage(1);
  };

  // SKS alt görünümü değiştirme
  // Mudurluk listesi Redux'tan otomatik geliyor — burada sadece state geçişi.
  const handleSksViewChange = (newView) => {
    setSksView(newView);
    setPage(1);
    if (newView === 'genel') {
      setSksBolge('');
    }
  };

  const openDetail = async (userCode) => {
    if (!userCode) return;
    setDetailModal({ open: true, data: null, loading: true });
    try {
      const res = await apiClient.get('/rule-engine/leaderboard/user-detail', {
        params: { period_id: period.id, user_code: userCode, tab: tab === 'sks' ? 'SKS' : 'MANAGER' },
      });
      setDetailModal({ open: true, data: res.data?.data ?? res.data, loading: false });
    } catch {
      setDetailModal({ open: false, data: null, loading: false });
    }
  };

  const totalPages = Math.ceil(total / SIZE);

  return (
    <Card padded={false}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-200">
        <div>
          <h3 className="text-base font-semibold">Sıralama</h3>
          <p className="text-xs text-neutral-500">{total} katılımcı · dönem bazlı sıralama</p>
        </div>
        <Button
          variant="secondary"
          icon="arrowRight"
          onClick={() => navigate(`/leaderboard?period_id=${period.id}`)}
        >
          Tam Sıralamayı Gör
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-neutral-200 px-4 gap-1 pt-2">
        {[
          { key: 'sks', label: 'Genel Sıralama' },
          { key: 'manager', label: 'Yönetici Sıralama' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => handleTabChange(key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? 'border-primary-500 text-primary-700'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* SKS alt görünümü toggle */}
      {tab === 'sks' && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-neutral-100 bg-neutral-50/60">
          <div className="flex h-11 rounded-lg border border-neutral-200 overflow-hidden text-xs font-medium">
            <button
              type="button"
              onClick={() => handleSksViewChange('genel')}
              className={`h-full px-3 transition-colors ${
                sksView === 'genel' ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              Tümü
            </button>
            <button
              type="button"
              onClick={() => handleSksViewChange('grupici')}
              className={`h-full px-3 transition-colors border-l border-neutral-200 ${
                sksView === 'grupici' ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              Grup İçi
            </button>
          </div>
          {sksView === 'grupici' && (
            <select
              value={sksBolge}
              onChange={(e) => { setSksBolge(e.target.value); setPage(1); }}
              className="h-11 rounded-lg border border-[#CBCECD] bg-white px-3 text-sm font-medium text-gray-900 min-w-[14rem] appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Müdürlük seçin…</option>
              {bolgeOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Grup içi — bölge seçilmemişse yönlendirici */}
      {tab === 'sks' && sksView === 'grupici' && !sksBolge ? (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
          <Icon name="layers" className="h-7 w-7 text-neutral-300" />
          <p className="text-sm font-semibold text-neutral-600">Grup İçi Sıralama</p>
          <p className="text-xs text-neutral-400 max-w-xs">
            Yukarıdan bir müdürlük seçerek o müdürlüğün grup içi sıralamasını görüntüleyin.
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-10">
          {period.status === 'draft' ? 'Taslak dönem — henüz hesaplanmadı.' : 'Henüz sıralama yok.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider w-14">
                  {tab === 'sks' && sksView === 'grupici' ? 'Grup İçi Sıra' : 'Sıra'}
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  {tab === 'sks' ? 'Oyuncu' : 'Yönetici'}
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider hidden md:table-cell">
                  {tab === 'sks' ? 'Bölge' : 'Bölge Müdürlüğü'}
                </th>
                {tab === 'sks' ? (
                  <>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">Puan</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider hidden sm:table-cell">ASPara</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">HYO %</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider hidden sm:table-cell">SKS</th>
                  </>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider w-20">Baraj</th>
                <th className="w-10 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((r) => {
                const isBelowBaraj = r.below_baraj;
                return (
                  <tr
                    key={tab === 'sks' ? r.user_id : r.manager_user_id}
                    className={`hover:bg-neutral-50 transition-colors ${isBelowBaraj ? 'bg-amber-50/40' : ''}`}
                  >
                    <td className="px-4 py-2.5">
                      <RankBadgeMini rank={tab === 'sks' && sksView === 'grupici' ? r.rank_bolge : r.rank} />
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-neutral-800 text-sm">
                        {tab === 'sks' ? r.user_name_masked : r.manager_name_masked}
                      </p>
                      <p className="text-[10px] text-neutral-400">
                        {tab === 'sks' ? r.user_id : r.manager_user_id}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-xs text-neutral-600">
                      {tab === 'sks' ? (r.mudurluk || r.bolge) : r.bolge_mudurlugu}
                    </td>
                    {tab === 'sks' ? (
                      <>
                        <td className="px-4 py-2.5 text-right">
                          <span className="font-bold text-primary-700">{r.total_points?.toFixed(1)}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                          {r.estimated_aspara > 0
                            ? <span className="text-xs font-semibold text-success-700">{formatASPara(r.estimated_aspara)}</span>
                            : <span className="text-xs text-neutral-400">—</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`font-bold text-sm ${
                            r.hyo_yuzde >= 85 ? 'text-success-700' : r.hyo_yuzde >= 75 ? 'text-warning-700' : 'text-danger-700'
                          }`}>%{r.hyo_yuzde?.toFixed(1)}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                          <span className="text-sm font-medium">{r.sks_count}</span>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2.5">
                      {isBelowBaraj && <Badge color="red" size="sm">Baraj Altı</Badge>}
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => openDetail(tab === 'sks' ? r.user_id : r.manager_user_id)}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-primary-600 transition-colors"
                        title="Puan detayını gör"
                      >
                        <Icon name="eye" className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200 text-xs text-neutral-500">
          <span>{total} kayıt · sayfa {page}/{totalPages}</span>
          <div className="flex gap-1">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-neutral-50"
            >
              ‹ Önceki
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-neutral-50"
            >
              Sonraki ›
            </button>
          </div>
        </div>
      )}

      {/* Footer note */}
      {rows.some((r) => r.below_baraj) && (
        <div className="border-t border-neutral-200 px-4 py-2 bg-neutral-50 text-xs text-neutral-500">
          Baraj altındaki oyuncular ödül almaz.
        </div>
      )}

      <UserDetailModal
        open={detailModal.open}
        onClose={() => setDetailModal({ open: false, data: null, loading: false })}
        detail={detailModal.data}
        loading={detailModal.loading}
        tab={tab}
      />
    </Card>
  );
}

/**
 * Rozet küçük önizlemesi — Bearer token gerektiren image endpoint'ini
 * axios ile blob olarak çekip objectURL üzerinden gösterir.
 */
function BadgeThumbSmall({ badgeId, size = 24 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!badgeId) return;
    let objectUrl;
    apiClient
      .get(`/rule-engine/badges/${badgeId}/image`, { responseType: 'blob' })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [badgeId]);

  if (!src) {
    return (
      <div
        style={{ width: size, height: size }}
        className="flex-shrink-0 rounded bg-neutral-200"
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: size, height: size }}
      className="flex-shrink-0 rounded object-contain"
    />
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DryRunPanel — Dönem detay sayfasında son dry-run simülasyon sonuçları
// ─────────────────────────────────────────────────────────────────────────────
function DryRunPanel({ period, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('SKS'); // 'SKS' | 'MANAGER'
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (selectedTab) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get('/rule-engine/leaderboard/dry-run', {
          params: { period_id: period.id, tab: selectedTab },
        });
        // apiClient interceptor envelope'u açar: res.data = { meta, data }
        const d = res.data ?? {};
        setMeta(d.meta ?? null);
        setRows(d.data ?? []);
      } catch (e) {
        setError('Dry-run sonuçları yüklenemedi.');
      } finally {
        setLoading(false);
      }
    },
    [period.id],
  );

  // Panel ilk açıldığında yükle
  useEffect(() => {
    if (open) load(tab);
  }, [open, tab, load, refreshKey]);

  const handleTabChange = (t) => {
    setTab(t);
    load(t);
  };

  return (
    <Card>
      {/* Başlık — tıklanabilir toggle */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <Icon name="beaker" className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold text-neutral-800">Son Dry-Run Simülasyon Sonuçları</span>
          {meta && (
            <span className="text-xs text-neutral-400 ml-2">
              {meta.snapshot_at
                ? new Date(meta.snapshot_at).toLocaleString('tr-TR')
                : 'Hesaplanıyor…'} — {meta.user_count} kullanıcı
            </span>
          )}
        </div>
        <Icon
          name={open ? 'chevronUp' : 'chevronDown'}
          className="w-4 h-4 text-neutral-400"
        />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          {/* Uyarı banner */}
          <div className="flex items-start gap-2 rounded-lg bg-violet-50 border border-violet-200 px-4 py-3">
            <Icon name="info" className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-violet-700">
              Bu sonuçlar bir <strong>simülasyondur</strong> — hiçbir puan veritabanına yazılmamış, ASPara dağıtılmamıştır.
              Dönem hesaplaması çalıştırıldığında gerçek sonuçlar Liderlik Tablosu'nda görünür.
            </p>
          </div>

          {/* Meta bilgi */}
          {meta && (
            <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
              <span><span className="font-medium text-neutral-700">Job:</span> {meta.job_id}</span>
              <span><span className="font-medium text-neutral-700">Tetikleyen:</span> {meta.triggered_by || '—'}</span>
              <span><span className="font-medium text-neutral-700">Süre:</span> {meta.duration_s?.toFixed(1)}s</span>
            </div>
          )}

          {/* Tab seçici */}
          <div className="flex gap-1 border-b border-neutral-200">
            {['SKS', 'MANAGER'].map((t) => (
              <button
                key={t}
                onClick={() => handleTabChange(t)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t
                    ? 'border-b-2 border-primary-500 text-primary-600'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                {t === 'SKS' ? 'Genel Sıralama' : 'Yönetici HYO'}
              </button>
            ))}
          </div>

          {/* İçerik */}
          {loading && (
            <div className="py-8 text-center text-sm text-neutral-400">Yükleniyor…</div>
          )}
          {error && !loading && (
            <div className="py-6 text-center text-sm text-red-500">{error}</div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="py-8 text-center space-y-1">
              <Icon name="beaker" className="w-8 h-8 text-neutral-300 mx-auto" />
              <p className="text-sm text-neutral-400">Henüz dry-run çalıştırılmamış.</p>
              <p className="text-xs text-neutral-400">
                Hesapla → Dry-Run modunu seçin; sonuçlar burada görünür.
              </p>
            </div>
          )}
          {!loading && !error && rows.length > 0 && tab === 'SKS' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                    <th className="py-2 pr-3 font-medium w-10">Sıra</th>
                    <th className="py-2 pr-3 font-medium">Kullanıcı</th>
                    <th className="py-2 pr-3 font-medium">Bölge / Müdürlük</th>
                    <th className="py-2 pr-3 font-medium text-right">Puan</th>
                    {/* ASPara kolonu kaldirildi — dry-run simulasyon oldugu icin */}
                    {/* gercek odul dagitilmadigindan ASPara gosterimi yaniltici. */}
                    <th className="py-2 font-medium text-center">Baraj</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.user_id}
                      className={`border-b border-neutral-100 ${r.below_baraj ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 pr-3 font-semibold text-neutral-700">{r.rank}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-neutral-800">{r.user_name_masked || r.user_id}</div>
                        <div className="text-neutral-400">{r.user_id}</div>
                      </td>
                      <td className="py-2 pr-3 text-neutral-500">
                        <div>{r.bolge || '—'}</div>
                        <div className="text-neutral-400">{r.mudurluk || ''}</div>
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold text-primary-700">
                        {r.total_points.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="py-2 text-center">
                        {r.below_baraj ? (
                          <Badge color="red" size="sm">Altında</Badge>
                        ) : (
                          <Badge color="green" size="sm">Geçti</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && !error && rows.length > 0 && tab === 'MANAGER' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                    <th className="py-2 pr-3 font-medium w-10">Sıra</th>
                    <th className="py-2 pr-3 font-medium">Yönetici</th>
                    <th className="py-2 pr-3 font-medium">Bölge Müdürlüğü</th>
                    <th className="py-2 pr-3 font-medium text-right">SKS</th>
                    <th className="py-2 pr-3 font-medium text-right">Hedef</th>
                    <th className="py-2 pr-3 font-medium text-right">Gerçekleşen</th>
                    <th className="py-2 pr-3 font-medium text-right">HYO %</th>
                    <th className="py-2 font-medium text-center">Baraj</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.user_id}
                      className={`border-b border-neutral-100 ${r.below_baraj ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 pr-3 font-semibold text-neutral-700">{r.rank}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-neutral-800">{r.user_name_masked || r.user_id}</div>
                        <div className="text-neutral-400">{r.user_id}</div>
                      </td>
                      <td className="py-2 pr-3 text-neutral-500">{r.mudurluk || '—'}</td>
                      <td className="py-2 pr-3 text-right text-neutral-600">{r.sks_count}</td>
                      <td className="py-2 pr-3 text-right text-neutral-600">
                        {r.hedef_puan?.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-primary-700">
                        {r.gerceklesen_puan?.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold">
                        <span className={r.hyo_yuzde >= 100 ? 'text-green-600' : r.hyo_yuzde >= 70 ? 'text-amber-600' : 'text-red-500'}>
                          %{r.hyo_yuzde?.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        {r.below_baraj ? (
                          <Badge color="red" size="sm">Altında</Badge>
                        ) : (
                          <Badge color="green" size="sm">Geçti</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
